import { FC, useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { useIntersection } from '../hooks/useIntersection';
import { button, colors, font } from "../lib/sharedStyles";
import SearchspringTileSwatches from "./SearchspringTileSwatches";
import CheckboxIcon from "./Icons/Checkbox";
import ModShopLogo from "./ModShop/ModShopLogo";
import { badgeTranslations } from "../lib/badgeTranslations";

const Tile = styled.a<{ $layout: string; $context?: string; }>`
  position: relative;
  display: flex;
  flex-direction: column;
  text-decoration: none;

  @media (min-width: 1024px) {
    background: linear-gradient(180deg, #E5E5E5 0%, #E5E5E5 13.7%, #EFF0F0 59.6%, #F7F7F7 100%);

    ${({ $layout }) => $layout == 'list' && `
      padding: 72px;
      flex-direction: row;
    `}

    ${({ $layout }) => $layout == 'grid' && `
      justify-content: space-between;
    `}

    &:hover {
      background: linear-gradient(180deg, #E5E5E5 -148.11%, #E5E5E5 -114.12%, #EFF0F0 -0.24%, #F7F7F7 100%);
    }

    ${({ $context }) => $context == 'searchFlyout' && `
      background: none;
      &:hover {
        background: none;
      }
    `}

    ${({ $layout, $context }) => $layout == 'grid' && $context != 'searchFlyout' && `
      height: 100%;
    `}
  }

  // Override specificity in Section Container styles
  .pill-badge {
    ${font('Instrument Sans', 600, '10px', '16px')}
  }

  // Hide injected Discount Ninja badge
  .limoniapps-discountninja-badge-wrapper {
    display: none;
  }
`;

interface ImageContainerProps {
  $context?: string;
  $isInstrument: boolean;
  $layout: string;
  $leftHanded: boolean;
  $noRotate: boolean;
  $noFilter: boolean;
  $isModShop: boolean;
  $showHoverImage?: boolean;
  $hoverRotateLeft?: boolean;
}

const ImageContainer = styled.div<ImageContainerProps>`
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #E5E5E5 0%, #E5E5E5 13.7%, #EFF0F0 59.6%, #F7F7F7 100%);

  ${({ $isInstrument, $isModShop }) => !$isInstrument && !$isModShop && `
    padding: 24% 16%;
  `}

  ${({ $layout }) => $layout == 'grid' && `
    aspect-ratio: 1 / 1.3;
    display: grid;
  `}

  ${({ $layout }) => $layout == 'list' && `
    aspect-ratio: 1 / 0.65;
    padding: 0 32px;
    display: flex;
    align-items: center;
  `}

  ${({ $noFilter, $layout }) => $layout == 'list' && !$noFilter && `
    overflow: initial;
  `}

  @media (min-width: 1024px) {
    ${({ $context }) => $context != 'searchFlyout' && `
      background: none;
    `}

    ${({ $layout }) => $layout == 'list' && `
      aspect-ratio: initial;
      flex: 0 0 42%;
      padding: 0;
    `}
  }

  img {
    max-width: 100%;
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: ${({ $noFilter }) => $noFilter ? 'none' : 'drop-shadow(20px 24px 14px rgba(0, 0, 0, 0.18))'};
    mix-blend-mode: ${({ $noFilter }) => $noFilter ? 'darken' : 'normal'};
    transition: opacity 300ms ease;

    // Hover Image
    &:nth-of-type(2) {
      opacity: 0;
      position: absolute;
      top: 0;
      left: 0;
    }

    ${({ $layout, $noRotate }) => $layout == 'grid' && !$noRotate && `
      &:nth-of-type(1) {
        transform: rotate(-90deg);
      }
      &:nth-of-type(2) {
        transform: rotate(90deg);
      }
    `}

    ${({ $layout, $leftHanded }) => $layout == 'grid' && $leftHanded && `
      &:nth-of-type(1) {
        transform: rotate(90deg);
      }
      &:nth-of-type(2) {
        transform: rotate(-90deg);
      }
    `}

    ${({ $layout }) => $layout == 'list' && `
      &:nth-of-type(2) {
        transform: rotate(180deg);
      }
    `}

    ${({ $isModShop }) => $isModShop && `
      &:nth-of-type(2) {
        filter: none;
        mix-blend-mode: darken;
      }
    `}

    ${({ $layout, $isModShop }) => $layout == 'list' && $isModShop && `
      transform: rotate(90deg);
      &:nth-of-type(2) {
        transform: rotate(90deg);
      }
    `}
  }

  ${({ $showHoverImage }) => $showHoverImage && `
    img:nth-of-type(1) {
      opacity: 0;
    }
    img:nth-of-type(2) {
      opacity: 1;
    }
  `}
`;

const ModShopLogoContainer = styled.div`
  position: absolute;
  z-index: 1;
  top: 16px;
  left: 16px;
`;

const PillBadge = styled.p`
  position: absolute;
  top: 16px;
  left: 16px;
  margin: 0;
  padding: 4px 16px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.56);
  letter-spacing: 0.1px;
  color: ${colors.fender.primaryDark};
  z-index: 2;
`;

const ProductInfoContainer = styled.div<{ $layout: string;  $context: string; }>`
  padding: 16px;

  @media (min-width: 1024px) {
    /* This accounts for the text badge placeholder */
    ${({ $layout, $context }) => $layout == 'grid' && $context != 'searchFlyout' && `
      padding-top: 0px;
    `}

    ${({ $layout }) => $layout == 'list' && `
      display: flex;
      flex: 0 0 58%;
      padding: 0 0 0 92px;
    `}
  }
`;

const ProductInfo = styled.div<{ $layout: string; }>`
  display: flex;
  flex-direction: column;
  background-color: ${colors.fender.white};
  @media (min-width: 1024px) {
    background-color: transparent;
    ${({ $layout }) => $layout == 'list' && `
      width: 100%;
    `}
  }

  // Name and Price
   p {
    color:  #191A1F;
    ${font('Instrument Sans', 600, '12px', '18px')}
    letter-spacing: 0.12px;

    @media (min-width: 1024px) {
      ${font('Instrument Sans', 600, '14px', '20px')}
      letter-spacing: 0.14px;
    }
  }

  .variant-count {
    color: #191A1F;
    ${font('Instrument Sans', 400, '12px', '18px')}
    letter-spacing: 0.12px;
  }
`;

const TextBadgeContainer = styled.div<{ $hide: boolean; }>`
  margin-bottom: 8px;

  @media (min-width: 1024px) {
    display: flex;

    ${({ $hide }) => $hide && `
      display: none;
    `}
  }

  span {
    display: none;

    @media (min-width: 1024px) {
      display: block;
    }
  }
`;

const TextBadge = styled.p`
  margin: 0;
  ${font('Instrument Sans', 600, '12px', '18px')}
  letter-spacing: 0.12px;
  color: ${colors.fender.accessibleGold};

  @media (min-width: 1024px) {
    &:not(:first-of-type) {
      display: flex;
      align-items: center;

      &::before {
        content: "";
        width: 1px;
        height: 12px;
        margin: 0 8px;
        background-color: rgba(25, 26, 31, 0.3);
      }
    }
  }
`;

const CoreText = styled.p<{ $layout: string; $hideDesktopList?: boolean; }>`
  margin: 0;
  ${font('Instrument Sans', 600, '12px', '18px')}
  letter-spacing: 0.12px;
  color: ${colors.fender.primaryDark};
  @media (min-width: 1024px) {
    ${({ $layout }) => $layout == 'grid' && `
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.14px;
    `}
    ${({ $layout }) => $layout == 'list' && `
      font-size: 18px;
      line-height: 24px;
      letter-spacing: 0.18px;
    `}
  }
`;

const Price = styled(CoreText)`
  @media (min-width: 1024px) {
    ${({ $layout, $hideDesktopList }) => $layout == 'list' && $hideDesktopList && `
      display: none;
    `}
  }
  // Discount Ninja injection overrides
  .limoniapps-discountninja-label-from {
    display: none;
  }
  // No way around important here – the plugin's css is pretty gnarly.
  > div.limoniapps-discountninja-productprice-replaced {
    .limoniapps-discountninja-strikethrough {
      order: -1;
      margin-left: 0;
      margin-right: 4px;
      .money {
        color: ${colors.fender.grey04} !important;
        font-weight: 400 !important;
      }
    }
    .limoniapps-discountninja-strikethrough, .limoniapps-discountninja-shortcode-discounted_price {
      .money {
        font-size: 12px !important;
        font-weight: 400 !important;
        // This is being applied at multiple levels,
        // making the line thicker than design.
        text-decoration: none !important;
        text-decoration-line: none !important;
        @media (min-width: 1024px) {
          font-size: 14px !important;
          ${({ $layout }) => $layout == 'list' && `
            font-size: 18px !important;
            font-weight: 400 !important;
            line-height: 24px !important;
            letter-spacing: 0.18px !important;
          `}
        }
      }
    }
    .limoniapps-discountninja-shortcode-discounted_price .money {
      color: ${colors.fender.primaryDark} !important;
      font-weight: 600 !important;
    }
  }
`;

const ProductName = styled(CoreText)`
  margin: 0 0 2px;
  @media (min-width: 1024px) {
    ${({ $layout }) => $layout == 'grid' && `
      margin-bottom: 4px;
    `}
    ${({ $layout }) => $layout == 'list' && `
      margin-bottom: 8px;
    `}
  }
`;

const VariantCount = styled.p<{ $layout: string; $isHovered: boolean; }>`
  display: none;
  margin: 0;
  ${font('Instrument Sans', 400, '12px', '18px')}
  letter-spacing: 0.12px;
  color: ${colors.fender.grey04};

  @media (min-width: 1024px) {
    ${({ $layout, $isHovered }) => $layout == 'grid' && !$isHovered && `
      display: block;
    `}
  }
`;

const AddToCart = styled.div<{ $layout: string; $hasBadges: boolean; }>`
  display: none;
  flex: 0 0 350px;
  padding-left: 100px;
  @media (min-width: 1024px) {
    ${({ $layout }) => $layout == 'list' && `
      display: block;
    `}
  }
  ${({ $hasBadges }) => $hasBadges && `
    padding-top: 26px;
  `}
  > p {
    margin-bottom: 16px;
  }
  button {
    appearance: none;
    border: none;
    width: 100%;
    ${button('Black Pill', 'fender', true)};
    margin-bottom: 16px;
  }
`;

const CompareBox = styled.div`
  position: absolute;
  z-index: 10;
  top: 8px;
  right: 8px;
  // The icon itself is not a large enough hitbox.
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CalloutMessage = styled.span<{ $hide?: boolean; }>`
  ${font('Instrument Sans', 400, '12px', '18px', '0.12px')};
  color: #14816e;

  ${({ $hide }) => $hide && `
    display: none;
  `}
`;

export interface TileVariant {
  id: string;
  outOfStockFlag: boolean;
  backInStockFlag: boolean;
  price: string;
  calloutMessage?: string;
  inventory: number;
  isNewVariant: boolean;
  pillBadgeOverride?: string;
  textBadges?: string[];
  imageUrls?: string[];
  swatchName?: string;
  swatchUrl?: string;
}

interface FenderProductTileProps {
  context?: string;
  compareClickHandler?: (e: any) => void;
  dataCategory?: string;
  fallbackImageUrls?: string[];
  isSelectedForCompare?: boolean;
  layout: string;
  locale: string;
  onClick?: () => void;
  productHandle: string;
  productId: string;
  productIsLeftHanded?: boolean;
  productName: string;
  productPrice: string;
  productSku: string;
  productTags?: string[];
  productType?: string;
  shopMarket?: string;
  showCompare?: boolean;
  region?: string;
  variants?: TileVariant[];
}

const FenderProductTile: FC<FenderProductTileProps> = ({
  context = '',
  compareClickHandler = () => {},
  dataCategory = '',
  fallbackImageUrls = [],
  isSelectedForCompare = false,
  layout,
  locale,
  onClick = () => {},
  productHandle,
  productId,
  productIsLeftHanded = false,
  productName,
  productPrice,
  productSku,
  productTags = [],
  productType = '',
  shopMarket = '',
  showCompare = false,
  region = '',
  variants = [],
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isHoveringOnSwatches, setIsHoveringOnSwatches] = useState(false);
  const [showSaleBadge, setShowSaleBadge] = useState(false);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const priceInCents = parseInt(productPrice, 10) * 100;
  const shopCurrencySymbol = document.getElementById('shop-currency-symbol')?.dataset?.currencySymbol || '$';
  const marketMetafieldsEl = document.getElementById('react-market-metafields');
  const disablePricing = (marketMetafieldsEl?.dataset?.disablePricing == 'true');

  const imgContainerRef = useRef<HTMLDivElement>(null);
  const isInViewOnLoad = useIntersection(imgContainerRef);

  let isInstrument = false;
  productTags?.forEach(tag => {
    // This logic may need to be expanded, but it seems to be working for all cases
    // that are currently visible on the site.
    if ((tag.includes('guitar') || tag.includes('bass')) && !tag.includes('amplifier') && productType != 'Guitar and Bass Parts') {
      isInstrument = true;
    }
  });

  let displayPrice = productPrice;
  if (variants.length > 0 && variants[activeVariantIndex].price) {
    displayPrice = variants[activeVariantIndex].price;
  }

  if (region == 'EMEA' && shopMarket != 'gb') {
    let formatter;

    if (shopMarket == 'dk') {
      formatter = new Intl.NumberFormat('da-DK', {style: 'currency', currency: 'DKK'});
    } else if (shopMarket == 'no') {
      formatter = new Intl.NumberFormat('no-NO', {style: 'currency', currency: 'NOK'});
    } else if (shopMarket == 'se') {
      formatter = new Intl.NumberFormat('en-SE', {style: 'currency', currency: 'SEK'});
    } else {
      formatter = new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'});
    }

    displayPrice = formatter.format(parseInt(displayPrice, 10));
  } else {
    displayPrice = `${shopCurrencySymbol}${displayPrice}`;
  }

  // @ts-ignore
  const discountHandler = (event) => {
    const discountInfo = event.detail.data[0];
    if (discountInfo.productHandle == productHandle && discountInfo.isDiscountedByOffer) {
      setShowSaleBadge(true);
    }
  }

  useEffect(() => {
    document.addEventListener('la:dn:product:discount:calculated', discountHandler);
    return () => { document.removeEventListener('la:dn:product:discount:calculated', discountHandler); }
  }, []);

  const isFmicAssetsImage = (url: string): boolean => {
    return url.includes('www.fmicassets.com');
  };

  const getResizedImageUrl = (url: string): string => {
    return isFmicAssetsImage(url) ? `https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=850/${url}` : `${url}`;
  };

  /**
   *
   * Pill Badge
   *
   */
  const activeVariantInventory = variants[activeVariantIndex]?.inventory;
  const outOfStockFlag = variants[activeVariantIndex]?.outOfStockFlag;
  const newVariantFlag = variants[activeVariantIndex]?.isNewVariant;
  const backInStockFlag = variants[activeVariantIndex]?.backInStockFlag;
  const pillOverride = variants[activeVariantIndex]?.pillBadgeOverride;

  let pillBadgeText = '';
  if (pillOverride) {
    pillBadgeText = pillOverride;
  } else if (newVariantFlag && activeVariantInventory == 0 && !outOfStockFlag) {
    // @ts-ignore
    pillBadgeText = badgeTranslations.comingSoon?.[locale] || 'Coming Soon'
  } else if (activeVariantInventory == 0 && !newVariantFlag) {
    if(region !== 'EMEA'){
      // @ts-ignore
      pillBadgeText = badgeTranslations.soldOut?.[locale] || 'Sold Out'
    }
  } else if (showSaleBadge) {
    // @ts-ignore
    pillBadgeText = badgeTranslations.sale?.[locale] || 'Sale'
  } else if (backInStockFlag) {
    // @ts-ignore
    pillBadgeText = badgeTranslations.backInStock?.[locale] || 'Back in Stock'
  } else if (newVariantFlag && activeVariantInventory > 0) {
    // @ts-ignore
    pillBadgeText = badgeTranslations.new?.[locale] || 'New'
  }

  // The default tile image is the front facing image for the selected variant;
  let baseImageUrl = variants[activeVariantIndex]?.imageUrls?.[0];
  let baseImageNoRotate = baseImageUrl?.includes('nr.png') ? true : false;
  // If this doesn't exist, attempt to use the fallback image.
  // This is used for products with images that are not fed into the PIM json field,
  // specifically Fender Play subscription products and bundles.
  if (!baseImageUrl && fallbackImageUrls?.length > 0) {
    baseImageUrl = fallbackImageUrls[0];
    baseImageNoRotate = true;
  }
  let baseImageNoFilter = baseImageUrl?.includes('.jpg') ? true : false;
  // If the hover image exists for this product, it will be at index 1 in the images array.
  let hoverImageUrl = variants[activeVariantIndex]?.imageUrls?.[1];
  if (!hoverImageUrl && fallbackImageUrls?.length > 1) {
    hoverImageUrl = fallbackImageUrls[1];
  }
  // Without the condition of hovering over swatches, the rear images
  // of the guitars will be displayed as the swatches are changed.
  let showHoverImage = (isHovered && !isHoveringOnSwatches);

  const isModShop = productName.includes('Mod Shop');
  const showPrice = !isModShop && !disablePricing;
  if (isModShop) {
    baseImageNoFilter = false;
    isInstrument = true;
  }

  const rotateLeftHandedImage = (productIsLeftHanded || baseImageUrl?.includes('rl.png')) || false;
  const hoverRotate = hoverImageUrl?.includes('rl.png') || false;

  // There are some complexities around the sizing of non-instrument hover images.
  // Disabling hover for those products until there is time to work it out.
  if (!isInstrument || !hoverImageUrl) {
    showHoverImage = false;
  }

  const hasTextBadges = (variants[activeVariantIndex]?.textBadges && variants[activeVariantIndex].textBadges.length > 0);

  return (
    <Tile
      href={`/products/${productHandle}`}
      $layout={layout}
      $context={context}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-category={dataCategory}
      data-sku={productSku}
      data-product-name={productName}
      data-la-dn-product-id={productId}
      data-la-dn-product-handle={productHandle}
      data-la-dn-product-price={priceInCents}
      onClick={onClick}
    >
      {(showCompare && !isModShop) && (
        <CompareBox onClick={compareClickHandler}>
          <CheckboxIcon filled={isSelectedForCompare} />
        </CompareBox>
      )}
      {isModShop && (
        <ModShopLogoContainer>
          <ModShopLogo withGradiant />
        </ModShopLogoContainer>
      )}
      {(pillBadgeText && !isModShop) && <PillBadge className="pill-badge">{pillBadgeText}</PillBadge>}

      <ImageContainer
        $layout={layout}
        $context={context}
        $noRotate={baseImageNoRotate}
        $isInstrument={isInstrument}
        $leftHanded={rotateLeftHandedImage}
        $noFilter={baseImageNoFilter}
        $isModShop={isModShop}
        $hoverRotateLeft={hoverRotate}
        $showHoverImage={showHoverImage}
        ref={imgContainerRef}
      >
        <img loading={isInViewOnLoad ? 'eager' : 'lazy'} alt={`${productName}-image`} src={getResizedImageUrl(baseImageUrl ? baseImageUrl : '')} />
        {hoverImageUrl && <img loading={isInViewOnLoad ? 'eager' : 'lazy'} alt={`${productName}-alternate-image`} src={getResizedImageUrl(hoverImageUrl ? hoverImageUrl : '')} />}
      </ImageContainer>

      <ProductInfoContainer $layout={layout} $context={context}>
        <ProductInfo $layout={layout}>
          {(variants.length > 1 && productType != 'Clothing') && (
            <SearchspringTileSwatches
              onMouseEnter={() => setIsHoveringOnSwatches(true)}
              onMouseLeave={() => setIsHoveringOnSwatches(false)}
              isHoverInfo={(layout == 'grid' && isHovered) || context == 'searchFlyout'}
              activeVariantIndex={activeVariantIndex}
              layout={layout}
              variants={variants.map(v => ({name: v.swatchName, url: v.swatchUrl}))}
              productHandle={productHandle}
              onSwatchHover={index => setActiveVariantIndex(index)}
            />
          )}
          <TextBadgeContainer $hide={(layout == 'grid' && isHovered) || context == 'searchFlyout'}>
            {(hasTextBadges) ? (
              <>
                {(variants[activeVariantIndex]?.textBadges?.map((badge, index) => (
                  <TextBadge key={`${productId}-${badge}-${index}`}>
                    {badge}
                  </TextBadge>
                )))}
              </>
            ) : (
              <span>&nbsp;</span>
            )}
            {/* The above keeps spacing consistent across tiles in rows where some have badges and some do not. */}
          </TextBadgeContainer>
          <ProductName $layout={layout} className="product-name">{productName}</ProductName>
          {showPrice && (
            <Price $layout={layout} $hideDesktopList data-la-dn-price>
              {displayPrice}
            </Price>
          )}
          {(variants.length > 1 && context != 'searchFlyout' && productType != 'Clothing') && (
            <VariantCount $layout={layout} $isHovered={isHovered} className="variant-count">
              {variants.length} Colors
            </VariantCount>
          )}
          {(variants[activeVariantIndex]?.calloutMessage) && (
            <CalloutMessage $hide={layout == 'list' || (layout == 'grid' && isHovered)}>
              {variants[activeVariantIndex]?.calloutMessage}
            </CalloutMessage>
          )}
        </ProductInfo>
        <AddToCart $layout={layout} $hasBadges>
          {showPrice && (
            <Price $layout={layout} data-la-dn-price>
              {displayPrice}
            </Price>
          )}
          <button>View details</button>
          {(variants[activeVariantIndex]?.calloutMessage) && (
            <CalloutMessage>
              {variants[activeVariantIndex]?.calloutMessage}
            </CalloutMessage>
          )}
        </AddToCart>
      </ProductInfoContainer>
    </Tile>
  );
}

export default FenderProductTile;
